<script lang="ts">
  import Self from './CommentItem.svelte';
  import type { CommentType, UserType, Commentable } from './types';
  import VoteButtons from './VoteButtons.svelte';
  import ReplyForm from './ReplyForm.svelte';
  import EditForm from './EditForm.svelte';
  import { commentService } from '../../services/commentService';
  import { ApiError } from '@services/api';
  import { format } from 'timeago.js';

  type Props = {
    comment: CommentType;
    currentUser: UserType | null;
    commentable: Commentable;
    replyingTo: number | null;
    onReplyClick: (commentId: number) => void;
    onReplyCancel: () => void;
    onReplySubmitted: (comment: CommentType) => void;
    onError: (error: Error) => void;
    loading: boolean;
    onVote: (comment: CommentType) => void;
    onDelete: (commentId: number) => void;
    level?: number;
  };

  const {
    comment,
    currentUser,
    commentable,
    replyingTo,
    onReplyClick,
    onReplyCancel,
    onReplySubmitted,
    onError,
    loading,
    onVote,
    onDelete,
    level = 0,
  }: Props = $props();

  let deleting = $state(false);
  let editing = $state(false);
  let showReplies = $state(false);
  let edited = $state(comment.updated_at > comment.created_at);

  async function handleDelete() {
    if (deleting) return;
    deleting = true;
    try {
      await commentService.deleteComment(comment.id);
      onDelete(comment.id);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        onError(e);
      } else {
        onError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      deleting = false;
    }
  }

  function toggleReplies() {
    showReplies = !showReplies;
  }

  function handleEdit() {
    editing = true;
  }

  function handleCancelEdit() {
    editing = false;
  }

  function handleUpdateComment(updatedComment: CommentType) {
    // Update the current comment with the new data
    comment.body = updatedComment.body;
    comment.updated_at = updatedComment.updated_at;
    edited = true;
    editing = false;
  }

  const isReply = $derived(comment.is_reply || comment.parent_id !== null);
  const isElder = $derived(comment.user && comment.user.elder);

  const replies = $derived(comment.replies || []);
  const repliesCount = $derived(replies.length);
  const hasReplies = $derived(repliesCount > 0);
  const showRepliesIcon = $derived(
    showReplies ? 'fa-chevron-up' : 'fa-chevron-down',
  );
</script>

<div class="comment {isReply ? 'reply' : ''}">
  <div class="comment-content">
    <div class="comment-header">
      <a href={`/u/${comment.user.username.toLowerCase()}`} class="username">
        {#if comment.user.avatar}
          <img
            class="avatar"
            src={comment.user.avatar}
            alt={comment.user.username}
          />
        {/if}
        <span class="username">
          {comment.user?.username || 'Anonymous'}
        </span>
      </a>

      {#if isElder}
        <span class="role-badge" title="Élder">
          <i class="fas fa-pen-nib"></i>
        </span>
      {/if}

      <span class="timestamp">
        {format(comment.created_at, 'pt_BR')}
      </span>
    </div>

    {#if editing}
      <EditForm
        {comment}
        onCancel={handleCancelEdit}
        onUpdate={handleUpdateComment}
        {onError}
      />
    {:else}
      <div class="comment-body">
        {#if isReply && comment.parent_username}
          <span class="parent-tag">@{comment.parent_username}</span>
        {/if}
        {comment.body}
        {#if edited}
          <span class="edited-tag">(Editado)</span>
        {/if}
      </div>

      <div class="interaction-row">
        <VoteButtons
          {comment}
          loading={loading || deleting}
          vote={onVote}
          {onError}
        />

        <div class="comment-actions">
          <button
            class="action-btn"
            onclick={() => onReplyClick(comment.id)}
            disabled={deleting || editing}
          >
            Responder
          </button>

          {#if currentUser && currentUser.id === comment.user.id}
            <button
              class="action-btn"
              onclick={handleEdit}
              disabled={deleting || editing}
            >
              Editar
            </button>
            <button
              class="action-btn delete-btn"
              onclick={handleDelete}
              disabled={deleting || editing}
              aria-busy={deleting}
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </button>
          {/if}
        </div>
      </div>
    {/if}

    {#if replyingTo === comment.id && !editing}
      <ReplyForm
        parentId={comment.id}
        {commentable}
        onCancel={onReplyCancel}
        onSubmit={onReplySubmitted}
        {onError}
      />
    {/if}

    {#if hasReplies && !editing}
      <button class="toggle-replies-btn action-btn" onclick={toggleReplies}>
        <span>
          {showReplies ? 'Ocultar' : 'Mostrar'}
          {repliesCount} resposta{repliesCount !== 1 ? 's' : ''}
        </span>
        <i class="show-replies-icon fas {showRepliesIcon}"></i>
      </button>
    {/if}

    {#if hasReplies && showReplies && !editing}
      <div class="replies">
        {#each replies as reply (reply.id)}
          <Self
            comment={reply}
            level={level + 1}
            {currentUser}
            {commentable}
            {replyingTo}
            {onReplyClick}
            {onReplyCancel}
            {onReplySubmitted}
            {onError}
            {loading}
            {onVote}
            {onDelete}
          />
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .comment {
    display: flex;
    padding: 12px 15px;
  }

  .comment-content {
    flex: 1;
  }
  .comment-header {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    font-size: 12px;
    gap: 8px;
  }
  .username {
    font-weight: 600;
    color: #1a1a1b;
    display: inline-flex;
    align-items: center;

    :global(.dark) :global(#chapter-section) & {
      color: #dedddd;
    }
  }
  .timestamp {
    color: #787c7e;

    :global(.dark) :global(#chapter-section) & {
      color: #aaaaaa;
    }
  }
  .role-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: rgb(22, 22, 22);
    margin-left: 2px;
    /* show at the top of the left text */
    position: relative;
    top: -2px;

    :global(.dark) :global(#chapter-section) & {
      color: #d1d1d7;
    }
  }
  .comment-body {
    margin-bottom: 8px;
    line-height: 1.6;
    word-wrap: break-word;
    font-size: 14px;
  }

  .edited-tag {
    font-size: 12px;
    color: #787c7e;
    margin-left: 4px;

    :global(.dark) :global(#chapter-section) & {
      color: #aaaaaa;
    }
  }

  .parent-tag {
    font-weight: 600;
    color: #0095f6;
    margin-right: 4px;

    :global(.dark) :global(#chapter-section) & {
      color: #f5c109;
    }
  }

  .interaction-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    margin-top: 6px;
  }

  .comment-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .action-btn {
    padding: 6px 8px;
    min-height: 30px;
    min-width: 44px;
    font-size: 12px;
    background: none;
    color: #878a8c;
    border: none;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #1a1a1b;

      :global(.dark) :global(#chapter-section) & {
        color: #f5c109;
      }
    }
  }

  .toggle-replies-btn {
    margin-top: 4px;
    margin-bottom: 8px;
    color: #0095f6;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
      color: #0079d3;

      :global(.dark) :global(#chapter-section) & {
        color: #d1a813;
      }
    }

    :global(.dark) :global(#chapter-section) & {
      color: #f5c109;
    }
  }

  .replies {
    border-left: 1px solid #eee;

    :global(.dark) :global(#chapter-section) & {
      border-left: 1px solid #636363;
    }
  }
</style>
